import { useState } from 'react';
import { useAccount, useConfig } from 'wagmi';
import { useApolloClient } from '@apollo/client';
import { useUserPositions, Position } from './useUserPositions';
import { usePoolData } from './usePoolData';
import { CONTRACTS } from '../config/constants';
import { UNISWAP_V3_POSITION_MANAGER_ABI, UNISWAP_V3_STAKER_ABI } from '../config/abis';
import {
  GET_INCENTIVES,
  GET_DEPOSIT_INFOS,
  GET_STAKE_INFOS,
  GET_USER_ALL_STAKE_INFOS,
} from '../config/queries';
import { formatUnits, keccak256 } from 'ethers';
import { getIncentiveKey, sortIncentivesByApr } from '../utils/common';
import { readContract, readContracts } from 'wagmi/actions';

interface IncentiveKey {
  id?: string;
  rewardToken: string;
  pool: string;
  startTime: bigint;
  endTime: bigint;
  refundee: string | undefined;
  reward: bigint;
}

export function useStakingData() {
  const wagmiConfig = useConfig();
  const client = useApolloClient();
  const { address } = useAccount();
  const [createdIncentives, setCreatedIncentives] = useState<IncentiveKey[]>(
    []
  );
  const [stakedPositions, setStakedPositions] = useState<Position[]>([]);
  const [stakeInfos, setStakeInfos] = useState<any>([]);
  const [rewards, setRewards] = useState<string>('');
  const [loadingUnstakedRewards, setLoadingUnstakedRewards] = useState<boolean>(false);
  const [loadingIncentives, setLoadingIncentives] = useState<boolean>(false);
  const [loadingStakeInfos, setLoadingStakeInfos] = useState<boolean>(false);

  const {
    positions,
    isLoading: loadingUserPositions,
    refetch: fetchUserPositions,
  } = useUserPositions();

  const poolData = usePoolData();

  const fetchUnstakedRewards = async () => {
    if (!address) return;
    setLoadingUnstakedRewards(true);

    const rewardsData = await readContract(wagmiConfig, {
      address: CONTRACTS.UNISWAP_V3_STAKER as `0x${string}`,
      abi: UNISWAP_V3_STAKER_ABI,
      functionName: 'rewards',
      args: [CONTRACTS.JOCX_TOKEN as `0x${string}`, address]
    })

    const formattedRewards = formatUnits(rewardsData, 18); // Assuming 18 decimals for JOCX
    setRewards(formattedRewards);

    setLoadingUnstakedRewards(false);
  }

  const fetchIncentives = async () => {
    setLoadingIncentives(true);

    const { data: incentives } = await client.query({ query: GET_INCENTIVES(poolData.poolAddress as string) });
    const tempIncentives: IncentiveKey[] = incentives.incentiveInfos.map(
      (i: any) => ({
        id: i.id,
        rewardToken: i.rewardToken,
        pool: i.pool,
        startTime: BigInt(i.startTime),
        endTime: BigInt(i.endTime),
        refundee: i.refundee,
        reward: BigInt(i.reward),
      })
    );

    // Sort incentives by APR (live first, then by APR rate)
    const sortedIncentives = sortIncentivesByApr(tempIncentives);
    setCreatedIncentives(sortedIncentives);
    setLoadingIncentives(false);
  };

  const fetchStakeInfos = async (incentive?: IncentiveKey) => {
    setLoadingStakeInfos(true);

    const { data: depositInfos } = await client.query({ query: GET_DEPOSIT_INFOS(address as string), fetchPolicy: 'network-only' });
    const tempIds = depositInfos.depositInfos.map(
      (depositInfo: any) => depositInfo.tokenId
    );

    if (tempIds.length === 0) {
      setStakeInfos([]);
      setStakedPositions([]);
      setLoadingStakeInfos(false);
      return;
    }

    // Fetch ALL stake infos for user's positions (without incentive filter)
    // This tells us which incentive each position is staked in
    const { data: allStakeData } = await client.query({
      query: GET_USER_ALL_STAKE_INFOS(tempIds),
      fetchPolicy: 'network-only'
    });
    const allStakeInfos = allStakeData?.stakeInfos || [];

    // Also fetch stake infos for the selected incentive (for backwards compatibility)
    let selectedIncentiveStakeInfos: any[] = [];
    if (incentive) {
      const { data } = await client.query({ query: GET_STAKE_INFOS(tempIds, keccak256(getIncentiveKey(incentive))), fetchPolicy: 'network-only' });
      setStakeInfos(data);
      selectedIncentiveStakeInfos = data.stakeInfos || [];
    }

    // Merge: use all stake infos to get complete picture
    const stakedInfos = allStakeInfos.length > 0 ? allStakeInfos : selectedIncentiveStakeInfos;

    const stakedPositionContracts = stakedInfos.map((stakedInfo: any) => ({
      address: CONTRACTS.UNISWAP_V3_POSITION_MANAGER as `0x${string}`,
      abi: UNISWAP_V3_POSITION_MANAGER_ABI,
      functionName: 'positions' as const,
      args: [BigInt(stakedInfo.tokenId)],
    }));


    // Fetch all staked position details at once
    const stakedPositionResults = await readContracts(wagmiConfig, {
      contracts: stakedPositionContracts
    });

    if (stakedPositionResults && stakedInfos.length > 0) {
      const validStakedPositions: Position[] = [];
      const seenTokenIds = new Set<string>(); // Track seen tokenIds to avoid duplicates

      stakedPositionResults.forEach((result, index) => {
        if (result.status === 'success' && result.result) {
          const tokenId = stakedInfos[index].tokenId;

          // Skip if we've already processed this tokenId (prefer staked over unstaked)
          if (seenTokenIds.has(tokenId)) {
            // If current is staked but previous wasn't, replace it
            const existingIndex = validStakedPositions.findIndex(p => p.tokenId === tokenId);
            if (existingIndex >= 0 && stakedInfos[index].staked && !validStakedPositions[existingIndex].staked) {
              validStakedPositions[existingIndex].staked = true;
              validStakedPositions[existingIndex].stakedIncentiveId = stakedInfos[index].incentiveId;
            }
            return;
          }

          const incentiveId = stakedInfos[index].incentiveId; // Get the incentive ID
          const [
            ,
            ,
            token0,
            token1,
            fee,
            tickLower,
            tickUpper,
            liquidity,
            ,
            ,
            tokensOwed0,
            tokensOwed1,
          ] = result.result as any;

          // Filter for JOCX/USDT positions only
          const isJocxUsdtPosition =
            (token0.toLowerCase() === CONTRACTS.JOCX_TOKEN.toLowerCase() &&
              token1.toLowerCase() === CONTRACTS.USDT_TOKEN.toLowerCase()) ||
            (token0.toLowerCase() === CONTRACTS.USDT_TOKEN.toLowerCase() &&
              token1.toLowerCase() === CONTRACTS.JOCX_TOKEN.toLowerCase());

          if (isJocxUsdtPosition && BigInt(liquidity) > BigInt(0)) {
            seenTokenIds.add(tokenId);
            validStakedPositions.push({
              tokenId,
              token0: token0 as string,
              token1: token1 as string,
              fee: Number(fee),
              tickLower: Number(tickLower),
              tickUpper: Number(tickUpper),
              liquidity: liquidity,
              tokensOwed0: tokensOwed0.toString(),
              tokensOwed1: tokensOwed1.toString(),
              staked: stakedInfos[index].staked,
              stakedIncentiveId: incentiveId, // Include which incentive this is staked in
            });
          }
        }
      });

      setStakedPositions(validStakedPositions);
    } else {
      setStakedPositions([]);
    }

    setLoadingStakeInfos(false);
  };

  return {
    // Data
    rewards,
    positions,
    stakedPositions,
    createdIncentives,
    stakeInfos,
    poolData,

    // Loading states
    loadingUserPositions,
    loadingStakeInfos,
    loadingIncentives,
    loadingUnstakedRewards,

    // Refetch functions (for external use)
    fetchUserPositions,
    fetchStakeInfos,
    fetchIncentives,
    fetchUnstakedRewards
  };
}
